/**
 * The area sidebar: pick a metric, read the ranking, open one area.
 *
 * One component for both granularities. The prose comes from AREA_NOUNS rather
 * than being written for districts, because saying "distrito" over a map of
 * barrios is the same mistake the map labels made — telling the reader they are
 * looking at something they are not.
 *
 * The detail replaces the list rather than stacking below it — the sidebar is
 * one column, and two stacked views is how the legacy panel became a long
 * scroll of two different things where the district you had just clicked was
 * below the fold.
 *
 * The ranking and the choropleth are driven by the same `metric` and the same
 * `colorFor`, so a row's swatch is always the colour that district is painted on
 * the map. They were two code paths in the legacy page, which is how an inverted
 * metric ended up ranked one way and painted the other.
 */

import type { Feature } from "geojson";

import { colorFor } from "./lib/choropleth";
import type { DistritoProps, Metric, RentaMeta } from "./lib/distritos";
import {
  areaContext, areaName, letterShares, metricsFor, num, rankDistritos,
  ratedDwellings, rentaDelta,
} from "./lib/distritos";
import { fmt, fmtPct } from "./lib/format";
import { LETTER_COLORS, LETTER_TEXT } from "./lib/letters";
import { count, str } from "./lib/props";
import type { AggregateVista } from "./lib/vista";
import { AREA_NOUNS } from "./lib/vista";

interface Props {
  features: readonly Feature[];
  renta: RentaMeta | null;
  /** Which granularity is painted; decides the metrics on offer and the prose. */
  vista: AggregateVista;
  /** Null paints no choropleth — the boundaries alone. */
  metric: Metric | null;
  onMetric: (metric: Metric | null) => void;
  /** The open district's key, or null while the ranking is showing. */
  selected: string | null;
  onSelect: (key: string) => void;
  onBack: () => void;
  breaks: readonly number[];
}

export function DistritoPanel({
  features, renta, vista, metric, onMetric, selected, onSelect, onBack, breaks,
}: Props) {
  const metrics = metricsFor(vista);
  const noun = AREA_NOUNS[vista];
  const open = selected === null
    ? null
    : features
      .map((f) => (f.properties ?? {}) as DistritoProps)
      .find((p) => str(p["key"]) === selected) ?? null;

  if (open) {
    return <DistritoDetail props={open} renta={renta} noun={noun} onBack={onBack} />;
  }

  const { rows, missing, max } = rankDistritos(features, metric);

  return (
    <div className="distrito-panel">
      <div className="metric-row">
        <label htmlFor="distrito-metric">Colorear y ordenar por</label>
        <select
          id="distrito-metric"
          value={metric?.key ?? ""}
          onChange={(e) => {
            onMetric(metrics.find((m) => m.key === e.target.value) ?? null);
          }}
        >
          {/* First, not last: it is the neutral state the others depart from,
              and a reader looking for "turn the colour off" looks at the top. */}
          <option value="">Sin métrica · sólo límites</option>
          {metrics.map((m) => (
            <option key={m.key} value={m.key}>{m.label}</option>
          ))}
        </select>
      </div>

      <ol className="rank-list">
        {rows.map((p, i) => {
          const value = metric ? num(p[metric.key]) : null;
          const color = metric ? colorFor(value, breaks, metric.lowFirst) : null;
          const key = str(p["key"]) ?? String(i);
          return (
            <li key={key}>
              <button type="button" className="rank-row" onClick={() => onSelect(key)}>
                {/* No rank number without a metric: the list is alphabetical
                    then, and numbering it would assert an order the data does
                    not have. */}
                {metric && <span className="rank-n">{i + 1}</span>}
                {color && <span className="rank-swatch" style={{ background: color }} />}
                <span className="rank-name">
                  {areaName(p)}
                  {/* Income doubles as the row's context line, under three
                      conditions. Not when there is no metric at all: the list is
                      then an index of what exists, and a figure beside each name
                      is the "more info" that state is defined by not having. Not
                      when income IS the ranked metric, where it would be printed
                      twice on the same row. And not where there is none — the
                      barrio layer carries no income (DATOS §D), so without that
                      guard all 88 rows read "· – €/hogar". */}
                  {metric !== null && metric.key !== "renta_neta_hogar"
                    && num(p["renta_neta_hogar"]) != null && (
                    <span className="rank-sub">
                      {" "}· {fmt(num(p["renta_neta_hogar"]), 0, "€/hogar")}
                    </span>
                  )}
                </span>
                {metric && (
                  <>
                    <span className="rank-bar-wrap">
                      <span
                        className="rank-bar"
                        style={{
                          // Never zero-width: an area with a real but tiny value
                          // would otherwise look like one with no value at all.
                          width: `${Math.max(2, Math.round((100 * (value ?? 0)) / max))}%`,
                          background: color ?? undefined,
                        }}
                      />
                    </span>
                    <span className="rank-val">{metric.format(value)}</span>
                  </>
                )}
              </button>
            </li>
          );
        })}
      </ol>

      {missing.length > 0 && (
        <p className="rank-missing">
          {missing.length} {missing.length > 1 ? noun.plural : noun.singular} sin
          dato para esta métrica: {missing.map(areaName).join(", ")}
        </p>
      )}
    </div>
  );
}

/**
 * Income, as its own card.
 *
 * Kept visually distinct from the energy figures because it comes from a
 * different source and a different year — the INE Atlas, not the cadastral
 * model — and the reader should never have to guess which numbers share a
 * provenance.
 */
function RentaBlock({ props: p, renta }: { props: DistritoProps; renta: RentaMeta | null }) {
  const value = num(p["renta_neta_hogar"]);
  if (value == null) return null;

  const city = renta?.municipio_renta_neta_hogar ?? null;
  const delta = rentaDelta(value, city);

  return (
    <section className="renta-card">
      <h3>Renta {renta?.ejercicio ?? ""}</h3>
      <p className="renta-val">
        <b>{fmt(value, 0, "€")}</b>
        <span>neta por hogar y año</span>
      </p>
      {delta != null && city != null && (
        <p className={delta >= 0 ? "renta-delta up" : "renta-delta down"}>
          {delta >= 0 ? "▲" : "▼"} {fmtPct(Math.abs(delta))} respecto a la media de
          València ({fmt(city, 0, "€")})
        </p>
      )}
      <p className="card-small italic">
        Atlas de distribución de renta de los hogares · INE
      </p>
    </section>
  );
}

const DEADLINES: { year: number; letters: string; countKey: string; pctKey: string }[] = [
  { year: 2030, letters: "letras F y G", countKey: "deadline_2030_FG", pctKey: "pct_2030" },
  { year: 2033, letters: "letra E", countKey: "deadline_2033_E", pctKey: "pct_2033" },
  { year: 2035, letters: "letra D", countKey: "deadline_2035_D", pctKey: "pct_2035" },
];

function DistritoDetail({
  props: p, renta, noun, onBack,
}: {
  props: DistritoProps;
  renta: RentaMeta | null;
  noun: (typeof AREA_NOUNS)[AggregateVista];
  onBack: () => void;
}) {
  const rated = ratedDwellings(p);
  const withData = count(p["total_con_datos"]);
  const ahorro = count(p["ahorro_potencial_total"]);

  return (
    <div className="distrito-detail">
      <div className="detail-back">
        <button type="button" onClick={onBack}>← {noun.todos}</button>
      </div>

      <header className="detail-head">
        <p className="card-eyebrow">{areaContext(p)}</p>
        <h2>{areaName(p)}</h2>
        <p className="card-foot">
          {fmt(num(p["total_edificios"]))} edificios catastrales ·{" "}
          {fmt(num(p["n_viviendas"]))} viviendas
        </p>
      </header>

      <div className="detail-body">
        <div className="stat-grid">
          <div className="stat-card">
            <span className="stat-val">{fmt(withData)}</span>
            <span className="stat-lbl">con datos energéticos</span>
          </div>
          <div className="stat-card">
            <span className="stat-val">{fmt(num(p["sin_datos"]))}</span>
            <span className="stat-lbl">industrial / agrícola</span>
          </div>
        </div>

        <RentaBlock props={p} renta={renta} />

        <section>
          <h3 className="summary-heading">Distribución por letra</h3>
          {/* Buildings here, not dwellings: this is the stock the EED deadlines
              below count, and mixing the two units inside one panel is how the
              same district appeared to have two different F–G shares. */}
          {letterShares(p).map((s) => {
            const buildings = count(p[`letra_${s.letter}`]);
            const pct = withData > 0 ? (100 * buildings) / withData : 0;
            return (
              <div className="letter-bar" key={s.letter}>
                <span
                  className="letter-chip"
                  style={{
                    background: LETTER_COLORS[s.letter],
                    color: LETTER_TEXT[s.letter],
                  }}
                >
                  {s.letter}
                </span>
                <span className="letter-track">
                  <span
                    className="letter-fill"
                    style={{
                      width: `${Math.round(pct)}%`,
                      background: LETTER_COLORS[s.letter],
                    }}
                  />
                </span>
                <span className="letter-count">
                  {fmt(buildings)} ({fmtPct(pct)})
                </span>
              </div>
            );
          })}
          {rated === 0 && <p className="card-small">Sin viviendas calificadas.</p>}
        </section>

        <section>
          <h3 className="summary-heading">Obligaciones EED · estimado</h3>
          {DEADLINES.map(({ year, letters, countKey, pctKey }) => (
            <div className={year === 2030 ? "deadline-item urgent" : "deadline-item"} key={year}>
              <p className="deadline-year">Antes de {year} · {letters}</p>
              <p className="deadline-desc">
                {fmt(num(p[countKey]))} edificios · {fmtPct(num(p[pctKey]))} del parque
              </p>
            </div>
          ))}
        </section>

        <div className="stat-grid four">
          <div className="stat-card">
            <span className="stat-val">{fmt(num(p["demanda_calef_kwh_m2"]), 1)}</span>
            <span className="stat-lbl">calefacción kWh/m²·año</span>
          </div>
          <div className="stat-card">
            <span className="stat-val">{fmt(num(p["demanda_refrig_kwh_m2"]), 1)}</span>
            <span className="stat-lbl">refrigeración kWh/m²·año</span>
          </div>
          <div className="stat-card">
            <span className="stat-val">{fmtPct(num(p["pct_geo_mejor"]))}</span>
            <span className="stat-lbl">edificios donde geotermia gana en LCOE</span>
          </div>
          <div className="stat-card">
            <span className="stat-val">{fmt(num(p["ahorro_por_vivienda"]), 0, "€")}</span>
            <span className="stat-lbl">ahorro potencial por vivienda</span>
          </div>
        </div>

        <p className="card-foot">
          Ahorro potencial del distrito:{" "}
          <b>
            {/* Millions once the figure runs to seven digits: the panel is 23rem
                wide and "1.284.500 €/año" wraps mid-number. */}
            {ahorro >= 1e6 ? `${fmt(ahorro / 1e6, 1)} M€/año` : fmt(ahorro, 0, "€/año")}
          </b>
        </p>
      </div>
    </div>
  );
}
